import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AsyncAdditionalAmount } from '@/pages/AdditionalAmount/async';
import { useClientConstraintsQuery } from '@/hooks/webapi/client/useClientConstraintsQuery';
import * as R from '@/Router/routes';
import { AsyncActiveLoan } from '@/pages/ActiveLoan/async';
import { AsyncOverdueLoan } from '@/pages/OverdueLoan/async';
import { AsyncProfile } from '@/pages/Profile/async';
import { AsyncRepeatedLoan } from '@/pages/RepeatedLoan/async';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import DashboardIcon from '@/assets/icons/dashboard.svg?react';
import MoneyIcon from '@/assets/icons/money.svg?react';
import ProfileIcon from '@/assets/icons/profile.svg?react';
import PromoIcon from '@/assets/icons/promo.svg?react';
import { isLogged } from '@/utils/auth/useAuthStatusMutation';
import {
  isAdditionalAmountDisabled,
  isMinMonthlyRepaymentDisabled,
} from '@/utils/calcConditions';
import { useClient, useLatestLoan } from './webapi';
import { SubMenuItem } from '@/components/Layouts/components/MobileHeader/MobileMenu';
import { AsyncRepayment } from '@/pages/Repayment/async';
import { AsyncPromo } from '@/pages/Promo/async';
import FAQIcon from '@/assets/icons/faq-menu.svg?react';
import LoanExamplesIcon from '@/assets/icons/loan-examples.svg?react';
import AsyncFAQ from '@/pages/FAQ/async';
import AsyncLoanExamples from '@/pages/LoanExamples/async';

type Props = {
  shouldFetchMenu?: boolean;
};

const useMenuItems = (
  { shouldFetchMenu }: Props = { shouldFetchMenu: true },
) => {
  const { latestLoan, fetchLatestLoan } = useLatestLoan();
  const { data: clientConstraints } = useClientConstraintsQuery();
  const { client, fetchClient } = useClient();
  const { t } = useTranslation(['menu', 'promo']);

  const isClientLogged = isLogged();

  const additionalAmountDisabled = isAdditionalAmountDisabled(
    latestLoan,
    client,
    clientConstraints,
  );

  const minMonthlyRepaymentDisabled = isMinMonthlyRepaymentDisabled(
    latestLoan?.earliestMtpPaymentDate,
  );

  const getFilteredSubMenu = useMemo((): SubMenuItem[] => {
    const list = [];

    if (!latestLoan?.overDueDays) {
      list.push({
        disabled: minMonthlyRepaymentDisabled,
        title: t('subMenu.minPayment'),
        url: R.REPAYMENT_MINIMUM,
      });
    }

    list.push({
      title: t('subMenu.customPayment'),
      url: R.REPAYMENT_CUSTOM,
    });

    list.push({
      title: t('subMenu.fulPayment'),
      url: R.REPAYMENT,
    });

    return list;
  }, [latestLoan?.overDueDays, minMonthlyRepaymentDisabled]);

  // data needed to enable/disable menuItems
  useEffect(() => {
    const loadData = async () => {
      const tasks = [];

      if (isClientLogged || latestLoan === undefined) {
        tasks.push(fetchLatestLoan());
      }
      if (shouldFetchMenu || client === undefined) {
        tasks.push(fetchClient());
      }

      if (tasks.length > 0) {
        try {
          await Promise.all(tasks);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Menu data loading failed:', error);
        }
      }
    };

    if (isClientLogged) loadData();
  }, [isClientLogged]);

  const menuItems = useMemo(() => {
    let dashboardURL = R.ACTIVE_LOAN;
    let dashboardAsync:
      | typeof AsyncActiveLoan
      | typeof AsyncRepeatedLoan
      | typeof AsyncOverdueLoan = AsyncActiveLoan;

    if (latestLoan == null) {
      dashboardURL = R.REPEATED_LOAN;
      dashboardAsync = AsyncRepeatedLoan;
    } else if (latestLoan.overDueDays > 0) {
      dashboardURL = R.OVERDUE_LOAN;
      dashboardAsync = AsyncOverdueLoan;
    }

    const isSubMenu = Boolean(
      !latestLoan?.isExtensionPossible ||
        latestLoan?.debtCollectLoanAgreement ||
        latestLoan?.hasActiveDebtSchedule ||
        !client?.hasOpenLoan,
    );

    return [
      {
        title: t(latestLoan ? 'active_loan' : 'new_loan'),
        url: dashboardURL,
        additionalActiveState: [R.OVERDUE_LOAN],
        icon: <DashboardIcon />,
        dataTest: 'dashboard',
        preload: dashboardAsync.preload,
      },
      {
        title: t('additional_amount'),
        url: R.ADDITIONAL_AMOUNT,
        disabled: additionalAmountDisabled,
        icon: <MoneyIcon />,
        dataTest: 'additional-amount',
        preload: AsyncAdditionalAmount.preload,
      },
      {
        title: t('extension'),
        disabled: isSubMenu,
        icon: <CalendarIcon />,
        dataTest: 'extension',
        preload: AsyncRepayment.preload,
        subMenu: getFilteredSubMenu,
      },
      {
        title: t('promo:title'),
        url: R.PROMO,
        icon: <PromoIcon />,
        dataTest: 'promo',
        preload: AsyncPromo.preload,
      },
      {
        title: t('profile'),
        url: R.PROFILE,
        icon: <ProfileIcon />,
        dataTest: 'profile',
        preload: AsyncProfile.preload,
      },
      {
        title: t('faq'),
        url: R.FAQ,
        icon: <FAQIcon />,
        dataTest: 'faq',
        preload: AsyncFAQ.preload,
      },
      {
        title: t('loan_examples'),
        url: R.LOAN_EXAMPLES,
        icon: <LoanExamplesIcon />,
        dataTest: 'loan-examples',
        preload: AsyncLoanExamples.preload,
      },
    ];
  }, [client?.hasOpenLoan, latestLoan]);

  return menuItems;
};

export default useMenuItems;
